import { useEffect } from "react";
import { v4 } from "uuid";
import { toast } from "sonner";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { Order } from "@/services/backend/htz/orders/types/order";
import {
  PatchPositionRequest,
  updateHtzOrderCache,
  useHtzOrderAddPositionMutation,
  useHtzOrderPatchPositionMutation,
  useHtzOrderRemovePositionMutation,
  useHtzOrderShowQuery,
} from "@/services/backend/htz/orders/api/order-api";
import {
  patchPosition,
  Position,
  positionPatchFromCatalog,
} from "@/services/backend/htz/position/types/position";
import { usePositionsTableDependencies } from "@/services/backend/htz/position/hooks/use-positions-table-dependencies";
import {
  PositionsTable,
  PositionsTableSkeleton,
} from "@/services/backend/htz/position/components/positions-table";
import { PositionRow } from "@/services/backend/htz/position/components/position-row";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";
import { PositionCatalog } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";

/**
 * OrderPositionsTable edits the positions of the order. Every change of a
 * position is saved on its own.
 */
export function OrderPositionsTable({ orderId }: { orderId: string }) {
  const { data: order, error } = useHtzOrderShowQuery({ id: orderId });
  const {
    catalog,
    airHandlingUnits,
    isLoading,
    error: dependenciesError,
  } = usePositionsTableDependencies(order?.companyId, order?.customerId);

  if (error || dependenciesError) {
    return <RTKQueryErrorAlert error={error ?? dependenciesError} />;
  }

  if (!order || !catalog || isLoading) {
    return <PositionsTableSkeleton />;
  }

  return (
    <Positions
      order={order}
      catalog={catalog}
      airHandlingUnits={airHandlingUnits}
    />
  );
}

function Positions({
  order,
  catalog,
  airHandlingUnits,
}: {
  order: Order;
  catalog: PositionCatalog;
  airHandlingUnits: AirHandlingUnit[];
}) {
  const [add, { error, reset }] = useHtzOrderAddPositionMutation();
  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  const disabled = isImmutable(order);

  return (
    <PositionsTable
      positions={order.positions}
      catalog={catalog}
      onAdd={(item) =>
        add({
          id: order.id,
          positionId: v4(),
          ...positionPatchFromCatalog(item),
        })
      }
      disabled={disabled}
    >
      {order.positions.map((position) => (
        <OrderPositionRow
          key={position.id}
          order={order}
          position={position}
          airHandlingUnits={airHandlingUnits}
          disabled={disabled}
        />
      ))}
    </PositionsTable>
  );
}

function OrderPositionRow({
  order,
  position,
  airHandlingUnits,
  disabled,
}: {
  order: Order;
  position: Position;
  airHandlingUnits: AirHandlingUnit[];
  disabled: boolean;
}) {
  const request: PatchPositionRequest = {
    id: order.id,
    positionId: position.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzOrderCache,
    useHtzOrderPatchPositionMutation(),
  );
  const [remove, { error, reset }] = useHtzOrderRemovePositionMutation();
  const { resetFields } = useErrValidationFailedContext();
  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  return (
    <PositionRow
      position={position}
      onPatch={(patch) =>
        set(
          { ...request, ...patch },
          {
            ...order,
            positions: patchPosition(order.positions, position.id, patch),
          },
        )
      }
      onRemove={() =>
        remove(request)
          .unwrap()
          .then(() => resetFields(joinFieldName("positions", position.id)))
          .catch(() => {
            // surfaced through the error state above
          })
      }
      airHandlingUnits={airHandlingUnits}
      disabled={disabled}
    />
  );
}
